import { CANDIDATE_STATUS, CandidateStatus } from 'services/candidates/types';

const LABEL_STATUS_MAPPING = {
  CONTACT_LABELS: ['contacted', 'leftVoicemail', 'moreInfoSent', 'noAnswer', 'notResponding'] as const,

  MATCH_LABELS: ['interviewScheduled', 'interviewCompleted'] as const,

  HIDDEN_LABELS: ['interviewCancelled', 'notInterested', 'notOpenToWork'] as const,
} as const;

const STATUSES_ELIGIBLE_FOR_MATCH = [
  CANDIDATE_STATUS.NEW,
  CANDIDATE_STATUS.SHORTLISTED,
  CANDIDATE_STATUS.CONTACTED,
  CANDIDATE_STATUS.HIDDEN,
] as const;

const STATUSES_ELIGIBLE_FOR_CONTACT = [
  CANDIDATE_STATUS.NEW,
  CANDIDATE_STATUS.SHORTLISTED,
  CANDIDATE_STATUS.HIDDEN,
] as const;

const STATUSES_ELIGIBLE_FOR_HIDDEN = [
  CANDIDATE_STATUS.NEW,
  CANDIDATE_STATUS.SHORTLISTED,
  CANDIDATE_STATUS.CONTACTED,
] as const;

export type CalculateCandidateStatusParams = {
  currentStatus: CandidateStatus;
  currentLabels: string[];
  newLabels: string[];
  fallbackStatus?: CandidateStatus | false | null | undefined;
};

export type CandidateStatusCalculationResult = {
  newStatus: CandidateStatus;
  addedLabels: string[];
  removedLabels: string[];
  statusChanged: boolean;
};

/**
 * Calculates the new candidate status based on label changes.
 *
 * This function implements the business logic for candidate status transitions:
 * - Adding interview-related labels (interviewScheduled, interviewCompleted) moves to MATCHED
 * - Adding rejection-related labels (interviewCancelled, notInterested, notOpenToWork) moves to HIDDEN
 * - Adding contact-related labels (contacted, leftVoicemail, moreInfoSent, noAnswer, notResponding) moves to CONTACTED
 * - Priority order: MATCHED > HIDDEN > CONTACTED (if multiple transitions are possible)
 * - Status transitions respect the current status and only allow valid transitions
 *
 * @param params - Object containing current status, current labels, and new labels
 * @returns Object with the new status and detailed change information
 *
 * @example
 * ```typescript
 * const result = calculateCandidateStatusFromLabels({
 *   currentStatus: 'NEW',
 *   currentLabels: ['qualified'],
 *   newLabels: ['qualified', 'contacted']
 * });
 * // result.newStatus === 'CONTACTED'
 * // result.addedLabels === ['contacted']
 * // result.statusChanged === true
 * ```
 */
export const calculateCandidateStatusFromLabels = ({
  currentStatus,
  currentLabels,
  newLabels,
  fallbackStatus,
}: CalculateCandidateStatusParams): CandidateStatusCalculationResult => {
  const removedLabels = currentLabels.filter((label) => !newLabels.includes(label));
  const addedLabels = newLabels.filter((label) => !currentLabels.includes(label));

  const canTransitionTo = (targetStatus: CandidateStatus): boolean => {
    if (targetStatus === CANDIDATE_STATUS.MATCHED) {
      return (STATUSES_ELIGIBLE_FOR_MATCH as readonly CandidateStatus[]).includes(currentStatus);
    }

    if (targetStatus === CANDIDATE_STATUS.CONTACTED) {
      return (STATUSES_ELIGIBLE_FOR_CONTACT as readonly CandidateStatus[]).includes(currentStatus);
    }

    if (targetStatus === CANDIDATE_STATUS.HIDDEN) {
      return (STATUSES_ELIGIBLE_FOR_HIDDEN as readonly CandidateStatus[]).includes(currentStatus);
    }

    return false;
  };

  const shouldMoveToMatch = addedLabels.some((label) => LABEL_STATUS_MAPPING.MATCH_LABELS.includes(label as any));
  const shouldMoveToHidden = addedLabels.some((label) => LABEL_STATUS_MAPPING.HIDDEN_LABELS.includes(label as any));
  const shouldMoveToContact = addedLabels.some((label) => LABEL_STATUS_MAPPING.CONTACT_LABELS.includes(label as any));

  let newStatus: CandidateStatus = currentStatus;

  // Priority: Match > Hidden > Contact
  if (shouldMoveToMatch && canTransitionTo(CANDIDATE_STATUS.MATCHED)) {
    newStatus = CANDIDATE_STATUS.MATCHED;
  } else if (shouldMoveToHidden && canTransitionTo(CANDIDATE_STATUS.HIDDEN)) {
    newStatus = CANDIDATE_STATUS.HIDDEN;
  } else if (shouldMoveToContact && canTransitionTo(CANDIDATE_STATUS.CONTACTED)) {
    newStatus = CANDIDATE_STATUS.CONTACTED;
  }

  if (fallbackStatus && (newStatus === CANDIDATE_STATUS.NEW || newStatus === CANDIDATE_STATUS.SHORTLISTED)) {
    newStatus = fallbackStatus ?? newStatus;
  }

  return {
    newStatus,
    addedLabels,
    removedLabels,
    statusChanged: newStatus !== currentStatus,
  };
};
